<template>
  <div class="p-6 md:p-10 bg-white min-h-screen font-sans text-slate-800 select-none">
    
    <!-- TOP BAR / HEADER -->
    <header class="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4 border-b border-slate-100 pb-6">
      <div>
        <div class="flex items-center gap-2 text-xs font-bold text-slate-400 mb-1">
          <span>Medialink Solutions</span>
          <span>/</span>
          <span class="text-blue-600 uppercase tracking-wider">Espace Officine</span>
        </div>
        <h1 class="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">
          {{ t.agency }} 
          <span class="text-blue-600">
            {{ agencyName }}
          </span>
        </h1>
      </div>
      
      <!-- STATUT AGENTS & RECHERCHE -->
      <div class="flex flex-wrap items-center gap-3 w-full lg:w-auto">
        


        <!-- Statut Agents Connectés -->
        <div class="flex items-center gap-2.5 bg-slate-50 border border-slate-200 px-4 py-2.5 rounded-xl">
          <div class="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></div>
          <span class="text-xs font-extrabold text-slate-700">{{ activeAgents }} {{ t.agentsOnline }}</span>
        </div>

        <!-- Badge Live -->
        <div class="inline-flex items-center gap-1.5 px-3.5 py-2.5 bg-blue-600 text-white text-xs font-bold rounded-xl shadow-lg shadow-blue-500/20">
          <span class="w-2 h-2 rounded-full bg-white animate-ping"></span>
          <span>{{ t.liveDashboard }}</span>
        </div>

      </div>
    </header>

    <!-- SECTION 1 : 4 CARTES KPI ÉPURÉES -->
    <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
      
      <!-- KPI 1 : Tickets Délivrés -->
      <div class="bg-slate-50/80 p-6 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all flex items-center justify-between">
        <div class="space-y-1">
          <p class="text-[11px] font-black uppercase tracking-widest text-slate-400">{{ t.ticketsIssued }}</p>
          <h3 class="text-3xl font-black text-slate-900">{{ ticketsToday }}</h3>
        </div>
        <div class="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shadow-sm">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
          </svg>
        </div>
      </div>

      <!-- KPI 2 : Temps Moyen d'Attente -->
      <div class="bg-slate-50/80 p-6 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all flex items-center justify-between">
        <div class="space-y-1">
          <p class="text-[11px] font-black uppercase tracking-widest text-slate-400">{{ t.avgWaitTime }}</p>
          <h3 class="text-3xl font-black text-slate-900">{{ avgWaitTime.toString().padStart(2, '0') }}<span class="text-base text-slate-400 font-bold ml-1">min</span></h3>
        </div>
        <div class="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shadow-sm">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>

      <!-- KPI 3 : Clients en Attente -->
      <div class="bg-slate-50/80 p-6 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all flex items-center justify-between">
        <div class="space-y-1">
          <p class="text-[11px] font-black uppercase tracking-widest text-slate-400">{{ t.waitingClients }}</p>
          <h3 class="text-3xl font-black text-amber-600">{{ ticketsWaiting }}</h3>
        </div>
        <div class="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600 shadow-sm">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
      </div>

      <!-- KPI 4 : Taux de Service -->
      <div class="bg-slate-50/80 p-6 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all flex items-center justify-between">
        <div class="space-y-1">
          <p class="text-[11px] font-black uppercase tracking-widest text-slate-400">{{ t.serviceRate }}</p>
          <h3 class="text-3xl font-black text-slate-900">{{ serviceRate }}<span class="text-base text-slate-400 font-bold ml-0.5">%</span></h3>
        </div>
        <div class="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shadow-sm">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>

    </div>

    <!-- SECTION 2 : TABLEAU DES GUICHETS -->
    <div class="bg-slate-50/50 rounded-2xl border border-slate-200/80 shadow-sm mb-8 overflow-hidden">
      <div class="p-6 border-b border-slate-200/80 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 class="text-lg font-black text-slate-900">{{ t.counterActivity }}</h3>
          <p class="text-xs text-slate-500 font-medium mt-0.5">Suivi en direct des postes d'appel et de la file active</p>
        </div>
        <span class="text-xs font-extrabold bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-xl shadow-sm">
          {{ countersPerformance.length }} {{ t.countersTotal || 'guichets' }}
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-white border-b border-slate-200/80">
              <th class="py-4 px-6">{{ t.tableCounter }}</th>
              <th class="py-4 px-6">{{ t.tableAgent }}</th>
              <th class="py-4 px-6 text-center">STATUT</th>
              <th class="py-4 px-6 text-center">{{ t.tableTreated }}</th>
              <th class="py-4 px-6 text-right">{{ t.tableAvgTime }}</th>
            </tr>
          </thead>
          <tbody class="text-xs divide-y divide-slate-200/60 bg-white">
            <tr v-for="counter in countersPerformance" :key="counter.id" class="hover:bg-slate-50/80 transition-colors">
              <td class="py-4 px-6 font-extrabold text-slate-900">{{ counter.name }}</td>
              <td class="py-4 px-6">
                <div class="flex items-center gap-2.5">
                  <div class="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700 font-bold text-xs border border-slate-200">
                    {{ counter.agent ? counter.agent[0] : '—' }}
                  </div>
                  <span class="font-bold" :class="counter.agent ? 'text-slate-800' : 'text-slate-400 italic'">
                    {{ counter.agent || t.unassigned || 'Non assigné' }}
                  </span>
                </div>
              </td>
              <td class="py-4 px-6 text-center">
                <span :class="counter.agent ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'" class="px-3 py-1 rounded-full text-[10px] font-extrabold border inline-block">
                  {{ counter.agent ? 'Actif' : 'Fermé' }}
                </span>
              </td>
              <td class="py-4 px-6 text-center">
                <span class="px-3 py-1 bg-slate-100 text-slate-800 rounded-lg font-extrabold text-xs">
                  {{ counter.treated }}
                </span>
              </td>
              <td class="py-4 px-6 text-right font-black text-blue-600">{{ counter.avgTime }} min</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- SECTION 3 : 3 COLONNES INFOS (TRAFIC, TIMELINE, PERIPHERIQUES) -->
    <div class="grid lg:grid-cols-3 gap-6">
      
      <!-- Graphique Trafic Horaire -->
      <div class="bg-slate-50/80 rounded-2xl p-6 border border-slate-200/80 shadow-sm flex flex-col justify-between">
        <div>
          <div class="flex justify-between items-center mb-6">
            <h3 class="text-sm font-black text-slate-900">{{ t.hourlyTraffic }}</h3>
            <span class="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider bg-white border border-slate-200 px-2.5 py-1 rounded-lg">Aujourd'hui</span>
          </div>
        </div>

        <div class="flex items-end justify-between gap-2 h-40 mb-2 px-1">
          <div 
            v-for="(val, i) in hourlyData" 
            :key="i" 
            class="flex-1 bg-blue-600 rounded-t-lg hover:bg-blue-700 transition-all cursor-pointer relative group"
            :style="{ height: val + '%' }"
          >
            <div class="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] font-bold py-1 px-2 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-md z-20 pointer-events-none">
              {{ val }} {{ t.clients }}
            </div>
          </div>
        </div>
        <div class="flex justify-between text-[10px] font-black text-slate-400 uppercase tracking-widest border-t border-slate-200 pt-3">
          <span>08h</span><span>10h</span><span>12h</span><span>14h</span><span>16h</span><span>18h</span>
        </div>
      </div>

      <!-- Timeline / Flux Live -->
      <div class="bg-slate-50/80 rounded-2xl p-6 border border-slate-200/80 shadow-sm">
        <h3 class="text-sm font-black text-slate-900 mb-6">Flux des Appels Récents</h3>
        <div class="space-y-4 relative pl-3 border-l-2 border-slate-200 ml-2 py-1">
          <div class="relative pl-4">
            <span class="absolute -left-[19px] top-1 w-2.5 h-2.5 rounded-full bg-blue-600 ring-4 ring-slate-50"></span>
            <p class="text-xs font-black text-slate-800">Nouveau ticket généré</p>
            <p class="text-[10px] text-slate-500 font-medium">Borne tactile principale</p>
          </div>
          <div class="relative pl-4">
            <span class="absolute -left-[19px] top-1 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-4 ring-slate-50"></span>
            <p class="text-xs font-black text-slate-800">Appel patient au Guichet 2</p>
            <p class="text-[10px] text-slate-500 font-medium">Ordonnances &amp; Conseils</p>
          </div>
          <div class="relative pl-4">
            <span class="absolute -left-[19px] top-1 w-2.5 h-2.5 rounded-full bg-amber-500 ring-4 ring-slate-50"></span>
            <p class="text-xs font-black text-slate-800">Patient en attente prolongée</p>
            <p class="text-[10px] text-slate-500 font-medium">Alerte temps d'attente</p>
          </div>
        </div>
      </div>

      <!-- Matériel & Recommandations -->
      <div class="space-y-6">
        
        <!-- Périphériques -->
        <div class="bg-slate-50/80 rounded-2xl p-6 border border-slate-200/80 shadow-sm">
          <div class="flex items-center justify-between mb-4">
            <h4 class="text-xs font-black uppercase tracking-widest text-slate-400">{{ t.deviceStatus }}</h4>
            <button @click="$router.push('/kiosk')" class="text-xs font-bold text-blue-600 hover:underline">Gérer</button>
          </div>
          <div class="space-y-2.5">
            <div v-for="device in devices" :key="device.name" class="flex items-center justify-between p-3 rounded-xl bg-white border border-slate-200/60 shadow-sm">
              <span class="text-xs font-bold text-slate-800">{{ device.name }}</span>
              <span :class="device.status === 'online' ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-rose-700 bg-rose-50 border-rose-200'" class="text-[9px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-lg border">
                {{ device.status === 'online' ? t.online : t.offline }}
              </span>
            </div>
          </div>
        </div>

      </div>

    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const translations = {
  fr: {
    liveDashboard: "Live Dashboard",
    agency: "Agence",
    subtitle: "Analyse des flux et performance des guichets en temps réel.",
    agentsOnline: "en ligne",
    ticketsIssued: "Tickets Délivrés",
    today: "Aujourd'hui",
    avgWaitTime: "Temps d'attente moy.",
    optimal: "Optimal",
    waitingClients: "Clients en attente",
    serviceRate: "Taux de Service",
    hourlyTraffic: "Affluence Horaire",
    trafficSubtitle: "Pics d'activité constatés sur la journée",
    last24h: "Dernières 24h",
    yesterday: "Hier",
    clients: "clients",
    counterActivity: "Activité par Guichet",
    countersTotal: "guichets",
    tableCounter: "Guichet",
    tableAgent: "Agent Actif",
    tableTreated: "Traités",
    tableAvgTime: "Durée moy.",
    unassigned: "Non assigné",
    deviceStatus: "État des Périphériques",
    online: "En ligne",
    offline: "Hors ligne",
    configHardware: "Configurer le matériel",
    optimization: "Optimisation",
    insightText: "Surveillez les pics d'affluence pour optimiser l'ouverture de vos guichets."
  },
  de: {
    liveDashboard: "Live-Dashboard",
    agency: "Agentur",
    subtitle: "Echtzeitanalyse von Flüssen und Schalterleistung.",
    agentsOnline: "online",
    ticketsIssued: "Ausgegebene Tickets",
    today: "Heute",
    avgWaitTime: "Durchschn. Wartezeit",
    optimal: "Optimal",
    waitingClients: "Wartende Kunden",
    serviceRate: "Servicequote",
    hourlyTraffic: "Stündlicher Andrang",
    trafficSubtitle: "Aktivitätsspitzen im Tagesverlauf",
    last24h: "Letzte 24h",
    yesterday: "Gestern",
    clients: "Kunden",
    counterActivity: "Schalteraktivität",
    countersTotal: "Schalter",
    tableCounter: "Schalter",
    tableAgent: "Aktiver Agent",
    tableTreated: "Bearbeitet",
    tableAvgTime: "Durchschn. Dauer",
    unassigned: "Nicht zugewiesen",
    deviceStatus: "Gerätestatus",
    online: "Online",
    offline: "Offline",
    configHardware: "Hardware konfigurieren",
    optimization: "Optimierung",
    insightText: "Überwachen Sie Besucherspitzen, um die Schalteröffnung zu optimieren."
  },
  it: {
    liveDashboard: "Dashboard Live",
    agency: "Agenzia",
    subtitle: "Analisi dei flussi e delle prestazioni degli sportelli in tempo reale.",
    agentsOnline: "online",
    ticketsIssued: "Biglietti emessi",
    today: "Oggi",
    avgWaitTime: "Tempo medio di attesa",
    optimal: "Ottimale",
    waitingClients: "Clienti in attesa",
    serviceRate: "Tasso di servizio",
    hourlyTraffic: "Affluenza oraria",
    trafficSubtitle: "Picchi di attività rilevati durante la giornata",
    last24h: "Ultime 24 ore",
    yesterday: "Ieri",
    clients: "clienti",
    counterActivity: "Attività per Sportello",
    countersTotal: "sportelli",
    tableCounter: "Sportello",
    tableAgent: "Agente Attivo",
    tableTreated: "Gestiti",
    tableAvgTime: "Durata media",
    unassigned: "Non assegnato",
    deviceStatus: "Stato periferiche",
    online: "Online",
    offline: "Offline",
    configHardware: "Configura hardware",
    optimization: "Ottimizzazione",
    insightText: "Monitora i picchi di affluenza per ottimizzare l'apertura degli sportelli."
  },
  en: {
    liveDashboard: "Live Dashboard",
    agency: "Agency",
    subtitle: "Real-time flow analysis and counter performance.",
    agentsOnline: "online",
    ticketsIssued: "Tickets Issued",
    today: "Today",
    avgWaitTime: "Avg. Wait Time",
    optimal: "Optimal",
    waitingClients: "Waiting Clients",
    serviceRate: "Service Rate",
    hourlyTraffic: "Hourly Traffic",
    trafficSubtitle: "Activity peaks observed during the day",
    last24h: "Last 24h",
    yesterday: "Yesterday",
    clients: "clients",
    counterActivity: "Counter Activity",
    countersTotal: "counters",
    tableCounter: "Counter",
    tableAgent: "Active Agent",
    tableTreated: "Processed",
    tableAvgTime: "Avg. Duration",
    unassigned: "Unassigned",
    deviceStatus: "Device Status",
    online: "Online",
    offline: "Offline",
    configHardware: "Configure hardware",
    optimization: "Optimization",
    insightText: "Monitor traffic peaks to optimize counter opening."
  }
};

const currentLang = ref(localStorage.getItem('appLanguage') || 'fr');
const t = computed(() => translations[currentLang.value] || translations.fr);

const agencyName = ref("-");
const activeAgents = ref(0);
const ticketsToday = ref(0);
const avgWaitTime = ref(0);
const ticketsWaiting = ref(0);
const serviceRate = ref(0);
const hourlyData = ref([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
const countersPerformance = ref([]);
const devices = ref([]);

const agencyId = localStorage.getItem('agencyId') || '';

const fetchDashboardData = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`https://api.datafuse.fr/api/agency/dashboard-stats/${agencyId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des données');
    }

    const data = await response.json();
    agencyName.value = data.agencyName;
    activeAgents.value = data.activeAgents;
    ticketsToday.value = data.ticketsToday;
    avgWaitTime.value = data.avgWaitTime;
    ticketsWaiting.value = data.ticketsWaiting;
    serviceRate.value = data.serviceRate;
    hourlyData.value = data.hourlyData;
    countersPerformance.value = data.countersPerformance;
    devices.value = data.devices;

  } catch (error) {
    console.error('Erreur:', error);
  }
};

let interval = null;

onMounted(() => {
  fetchDashboardData();
  interval = setInterval(fetchDashboardData, 30000);
});

onUnmounted(() => {
  if (interval) clearInterval(interval);
});
</script>